/**
 * 免拍地址抽查 · 展示口径与开关配置
 *
 * 只放**前端展示**相关的开关与标签样式。判定口径（分级阈值、聚合窗口、
 * 入池规则、周期粒度）一律由 quality 侧实现并随数据下发，不在此镜像一份 ——
 * 两处各写一份必然漂移，而前端这份还不参与判定，漂移了也无人察觉。
 *
 * plan：docs/plans/feature-team-pod-edge-case-report.md
 */
import {
    MonitorLevel,
    SampleJudgeResult,
    SpotTaskConclusion,
    SpotTaskTab,
} from '../types/ExemptSpotCheckType';


/** 抽查结论标签样式 */
export const CONCLUSION_TAG_COLOR: Record<SpotTaskConclusion, string> = {
    [SpotTaskConclusion.MAINTAIN]: 'success',
    [SpotTaskConclusion.OFFLINE]: 'error',
    [SpotTaskConclusion.OFFLINE_BLOCKED]: 'error',
};

/** 样本判定结果标签样式 */
export const JUDGE_RESULT_TAG_COLOR: Record<SampleJudgeResult, string> = {
    [SampleJudgeResult.PENDING]: 'default',
    [SampleJudgeResult.PASS]: 'success',
    [SampleJudgeResult.FAIL]: 'error',
};

/**
 * 可选的抽查结论列表。
 *
 * 三项：通过（维持生效）、不通过下线、不通过下线并禁止申报。
 * 后两项对豁免的效果相同，区别在是否连带禁止该上报账号再申报。
 *
 * 明确不做的两项（已定稿，勿再提）：
 * - 「部分通过」：围栏不支持部分剔除，抽查沿用终审的整体口径
 * - 「缩小围栏范围」处理动作：原型虽列出该动作，产品已确认不做。
 *   实现它需要主 R 侧支持「从围栏中剔除指定地址」，是跨侧新需求，不在本期范围
 */
export const getAvailableConclusions = (): SpotTaskConclusion[] => [
    SpotTaskConclusion.MAINTAIN,
    SpotTaskConclusion.OFFLINE,
    SpotTaskConclusion.OFFLINE_BLOCKED,
];

/**
 * 是否展示重罚数。
 *
 * 原型统计卡为「不通过 → 下线/重罚 14 / 6」，但方案第七章明确
 * 「重罚展示与罚款执行链路」不做，故只显示下线数。
 */
export const ENABLE_PENALTY_DISPLAY = false;

/**
 * 表格单元格「标红」的展示阈值。**仅用于染色，不参与任何判定。**
 *
 * 真正的分级由 quality 侧 `MonitorLevel.fromComplaintDeltaRate` 完成并随指标落库，
 * 前端 monitorLevel 列直接渲染后端下发值，不用这里的常量重算。
 * 两处取值刻意相同 —— 染色边界与分级边界一致才不会「标红但判正常」；
 * quality 侧调阈值时此处需同步。
 *
 * 判级只看客诉环比，客诉件数不参与，故此处不设件数阈值。
 */
export const MONITOR_THRESHOLDS = {
    /** 客诉 7 日环比 > 30% 高风险 */
    highRiskDeltaRate: 0.3,
    /** 客诉 7 日环比 > 15% 需关注 */
    attentionDeltaRate: 0.15,
};

/** 监控结论标签样式 */
export const MONITOR_LEVEL_TAG_COLOR: Record<MonitorLevel, string> = {
    [MonitorLevel.NORMAL]: 'success',
    [MonitorLevel.ATTENTION]: 'warning',
    [MonitorLevel.HIGH_RISK]: 'error',
};

/**
 * 是否为「不通过」（两档下线均属之）。
 *
 * 收口成函数而非各处写 `=== OFFLINE`：新增禁报档后，散落的单值判断会漏掉它，
 * 表现为「结论标签显示不通过，却归进已通过分组」这类自相矛盾。
 */
export const isOfflineConclusion = (conclusion?: SpotTaskConclusion): boolean =>
    conclusion === SpotTaskConclusion.OFFLINE || conclusion === SpotTaskConclusion.OFFLINE_BLOCKED;

/** 任务列表分组：待抽查 / 已通过 / 不通过 */
export const getAvailableTaskTabs = (): SpotTaskTab[] => [
    SpotTaskTab.PENDING,
    SpotTaskTab.PASSED,
    SpotTaskTab.FAILED,
];
